import type { RouteObject } from 'react-router'

import { RootLayout } from '@/app/layouts/RootLayout.tsx'
import { RouteErrorBoundary } from '@/app/layouts/RouteErrorBoundary.tsx'
import { RouteFallback } from '@/app/layouts/RouteFallback.tsx'

/**
 * Two trees from one definition.
 *
 * `/s/:tenantSlug/...` is a studio's real address. The unprefixed paths stay
 * as a development convenience: `npm run dev` opens at `/`, and every Phase
 * 1-5 route still resolves there, resolving the tenant from the subdomain,
 * the `?tenant=` parameter or the configured default instead.
 *
 * Both trees are built from the same children, so a screen cannot exist on one
 * and be missing from the other.
 */
function publicChildren(): RouteObject[] {
  return [
    {
      index: true,
      handle: { layout: 'wide' },
      lazy: async () => {
        const { HomePage } = await import('@/features/home/HomePage.tsx')
        return { Component: HomePage }
      },
    },
    {
      path: 'services',
      handle: { layout: 'wide' },
      lazy: async () => {
        const { ServicesPage } = await import('@/features/services/ServicesPage.tsx')
        return { Component: ServicesPage }
      },
    },
    {
      path: 'services/:serviceSlug',
      lazy: async () => {
        const { ServiceDetailPage } = await import(
          '@/features/services/ServiceDetailPage.tsx'
        )
        return { Component: ServiceDetailPage }
      },
    },
    {
      path: 'contacts',
      lazy: async () => {
        const { ContactsPage } = await import('@/features/business/ContactsPage.tsx')
        return { Component: ContactsPage }
      },
    },
    {
      path: 'install',
      lazy: async () => {
        const { InstallPage } = await import('@/features/install/InstallPage.tsx')
        return { Component: InstallPage }
      },
    },
    {
      path: 'account',
      lazy: async () => {
        const { AccountRoute } = await import('@/app/pages/AccountRoute.tsx')
        return { Component: AccountRoute }
      },
    },
    {
      path: 'booking/:accessToken',
      lazy: async () => {
        const { MyBookingScreen } = await import(
          '@/features/booking/screens/MyBookingScreen.tsx'
        )
        return { Component: MyBookingScreen }
      },
    },
    {
      path: '*',
      lazy: async () => {
        const { NotFoundPage } = await import('@/app/pages/NotFoundPage.tsx')
        return { Component: NotFoundPage }
      },
    },
  ]
}

function bookingChildren(): RouteObject[] {
  return [
    {
      index: true,
      lazy: async () => {
        const { BookingEntry } = await import('@/features/booking/screens/BookingEntry.tsx')
        return { Component: BookingEntry }
      },
    },
    {
      path: 'date',
      lazy: async () => {
        const { DateStep } = await import('@/features/booking/screens/DateStep.tsx')
        return { Component: DateStep }
      },
    },
    {
      path: 'time',
      lazy: async () => {
        const { TimeStep } = await import('@/features/booking/screens/TimeStep.tsx')
        return { Component: TimeStep }
      },
    },
    {
      path: 'contact',
      lazy: async () => {
        const { ContactStep } = await import('@/features/booking/screens/ContactStep.tsx')
        return { Component: ContactStep }
      },
    },
    {
      path: 'review',
      lazy: async () => {
        const { ReviewStep } = await import('@/features/booking/screens/ReviewStep.tsx')
        return { Component: ReviewStep }
      },
    },
    {
      path: 'success',
      lazy: async () => {
        const { SuccessScreen } = await import(
          '@/features/booking/screens/SuccessScreen.tsx'
        )
        return { Component: SuccessScreen }
      },
    },
  ]
}

function ownerChildren(): RouteObject[] {
  return [
    {
      index: true,
      lazy: async () => {
        const { OwnerDashboardPage } = await import(
          '@/features/owner/screens/OwnerDashboardPage.tsx'
        )
        return { Component: OwnerDashboardPage }
      },
    },
    {
      // Declared before 'bookings/:bookingId' so 'new' is never read as an id.
      path: 'bookings/new',
      lazy: async () => {
        const { OwnerNewBookingPage } = await import(
          '@/features/owner/screens/OwnerNewBookingPage.tsx'
        )
        return { Component: OwnerNewBookingPage }
      },
    },
    {
      path: 'bookings/:bookingId',
      lazy: async () => {
        const { OwnerBookingPage } = await import(
          '@/features/owner/screens/OwnerBookingPage.tsx'
        )
        return { Component: OwnerBookingPage }
      },
    },
    {
      path: 'services',
      lazy: async () => {
        const { OwnerServicesPage } = await import(
          '@/features/owner/screens/OwnerServicesPage.tsx'
        )
        return { Component: OwnerServicesPage }
      },
    },
    {
      path: 'photos',
      lazy: async () => {
        const { OwnerPhotosPage } = await import(
          '@/features/owner/screens/OwnerPhotosPage.tsx'
        )
        return { Component: OwnerPhotosPage }
      },
    },
    {
      path: 'settings',
      lazy: async () => {
        const { OwnerSettingsPage } = await import(
          '@/features/owner/screens/OwnerSettingsPage.tsx'
        )
        return { Component: OwnerSettingsPage }
      },
    },
  ]
}

const shared = {
  errorElement: <RouteErrorBoundary />,
  hydrateFallbackElement: <RouteFallback />,
} as const

/** Public shell, booking funnel and cabinet, all under one prefix. */
function treeFor(prefix: string): RouteObject[] {
  return [
    {
      ...shared,
      path: prefix === '' ? '/' : prefix,
      element: <RootLayout />,
      children: publicChildren(),
    },
    {
      ...shared,
      path: `${prefix}/book/:serviceSlug`,
      lazy: async () => {
        const { BookingLayout } = await import('@/features/booking/BookingLayout.tsx')
        return { Component: BookingLayout }
      },
      children: bookingChildren(),
    },
    {
      ...shared,
      path: `${prefix}/owner`,
      lazy: async () => {
        const { OwnerLayout } = await import('@/features/owner/OwnerLayout.tsx')
        return { Component: OwnerLayout }
      },
      children: ownerChildren(),
    },
  ]
}

export const routes: RouteObject[] = [
  // Studio-addressed routes first, so they win over the legacy tree.
  ...treeFor('/s/:tenantSlug'),
  ...treeFor(''),
]
